import { useEffect, useRef } from 'react';

/**
 * Calls handler when a mousedown happens outside every ref in `refs`.
 * Pass a single ref or an array of refs.
 *
 * Refs and handler are mirrored into a mutable ref from a passive effect so
 * callers can pass fresh arrays/functions on every render without re-binding
 * the document-level listener (which is expensive when many components do it).
 */
export const useClickOutside = (refs, handler, enabled = true) => {
  const latest = useRef({ refs, handler });

  // Sync after every render — never during it, which would be unsafe under
  // concurrent rendering.
  useEffect(() => {
    latest.current = { refs, handler };
  });

  useEffect(() => {
    if (!enabled) return;
    const onMouseDown = (event) => {
      const { refs: currentRefs, handler: currentHandler } = latest.current;
      const list = Array.isArray(currentRefs) ? currentRefs : [currentRefs];
      const insideAny = list.some((r) => r.current && r.current.contains(event.target));
      if (!insideAny) currentHandler(event);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [enabled]);
};
